import * as React from 'react'

import * as api from '@owl-nest/api-client/next'
import * as consent from '@owl-nest/consent'
import * as dateUtils from '@owl-nest/date'
import * as dateFns from '@owl-nest/date-fns'
import { t, tc, tn } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as env from '@owl-nest/config'
import * as utils from '@owl-nest/utils'
import * as plume from '@ulule/owl-kit-components/next'

export type NewsCardProps = {
  className?: string
  isEditMode: boolean
  isVideoActive?: boolean
  locale: api.Lang
  news: api.News
  onVideoMount?: () => void
  link: { href: string; onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void }
  project: api.Project
}

export function NewsCard({
  className,
  isEditMode,
  isVideoActive = true,
  link,
  onVideoMount,
  locale,
  news,
  project,
}: NewsCardProps): React.ReactElement {
  const preview = models.i18n.getWithLang(news.preview, locale, project.lang)
  const title = models.i18n.getWithLang(news.title, locale, project.lang)

  const userHasReadAccess = Boolean(news.permissions.self?.includes('read'))

  return (
    <plume.NewsCard
      className={className}
      content={preview}
      editingLink={`/projects/${project.id}/backoffice/news/${news.id}/`}
      link={link}
      isEditMode={isEditMode}
      locked={
        userHasReadAccess
          ? undefined
          : {
              fans: news.audience_type === api.NewsAudienceType.FANS,
              backers: [api.NewsAudienceType.PAYING, api.NewsAudienceType.SUPPORTERS].includes(news.audience_type),
              subscriptions: news.rewards?.map(
                (reward) =>
                  models.reward.title(reward as models.reward.Reward, project.lang) ??
                  utils.text.truncate(utils.text.htmlToText(models.i18n.get(reward.description) ?? ''), 50),
              ),
              tip: news.audience_type === api.NewsAudienceType.TIP,
            }
      }
      isVideoActive={isVideoActive}
      media={getMedia(news, project, locale)}
      onVideoMount={onVideoMount}
      date={getDisplayedDate(news, locale)}
      title={title ?? ''}
      translations={{
        commentCount: tn('%(count)s comment', '%(count)s comments', news.comments_count, {
          count: plume.accounting.formatNumber(news.comments_count),
        }),
        freeDonation: t('Free donation'),
        lockTooltip: getLockTooltip(),
        editCTA: t('Edit'),
        locked: getLockedLabel(),
        lockedIcon: getLockedIcon(),
        followCTA: t('Follow'),
        readCTA: t('See news'),
        subscribeCTA: models.project.isMembership(project)
          ? news.audience_type === api.NewsAudienceType.TIP && (news.rewards === null || news.rewards.length === 0)
            ? tc('Donate from [amount]', {
                amount: (
                  <plume.LocaleMoneyDisplayer
                    amount={1}
                    currency={project.currency}
                    localeFormat={locale}
                    rates={env.CURRENCIES_RATES}
                    targetCurrency={project.currency}
                  />
                ),
              })
            : tc('Subscribe from [amount] / month', {
                amount: (
                  <plume.LocaleMoneyDisplayer
                    amount={getLowestPriceRewards(news, project)}
                    currency={project.currency}
                    localeFormat={locale}
                    rates={env.CURRENCIES_RATES}
                    targetCurrency={project.currency}
                  />
                ),
              })
          : t('Contribute'),
      }}
    />
  )

  function getLockedLabel() {
    if (news.audience_type === api.NewsAudienceType.SUPPORTERS) {
      const forAllSupporters = news.rewards === null || news.rewards.length === 0
      if (project.type === api.ProjectType.Membership) {
        return forAllSupporters ? t('For members only') : t('For selected members')
      }
      return forAllSupporters ? t('For backers only') : t('For selected backers')
    }
    if (news.audience_type === api.NewsAudienceType.PAYING) {
      return t('For paying members')
    }
    if (news.audience_type === api.NewsAudienceType.FANS) {
      return t('For fans only')
    }
    if (news.audience_type === api.NewsAudienceType.TIP) {
      return t('For selected backers')
    }
  }

  function getLockedIcon() {
    if (news.audience_type === api.NewsAudienceType.SUPPORTERS) {
      const forAllSupporters = news.rewards === null || news.rewards.length === 0
      return forAllSupporters ? undefined : <plume.glyphs.stroke.Info size={12} />
    }

    if (news.audience_type === api.NewsAudienceType.TIP) {
      return <plume.glyphs.stroke.Info size={12} />
    }

    return undefined
  }

  function getLockTooltip() {
    if (news.audience_type === api.NewsAudienceType.SUPPORTERS) {
      const forAllSupporters = news.rewards === null || news.rewards.length === 0
      if (project.type === api.ProjectType.Membership) {
        return forAllSupporters ? t('For members only') : t('For following members')
      }
      return forAllSupporters ? t('For backers only') : t('For following backers')
    }
    if (news.audience_type === api.NewsAudienceType.PAYING) {
      return t('For paying members')
    }
    if (news.audience_type === api.NewsAudienceType.FANS) {
      return t('For fans only')
    }
    if (news.audience_type === api.NewsAudienceType.TIP) {
      return t('For following backers')
    }
  }
}

function getMedia(news: api.News, project: api.Project, locale: api.Lang): plume.Media | undefined {
  const video = news.video && models.i18n.getWithLang(news.video, locale, project.lang)
  const image = news.image && models.i18n.getWithLang(news.image, locale, project.lang)

  // news.contentType can't be used because it's related to the lang
  // whereas we want to mix translated contents here
  const contentType = video ? 'video' : image ? 'image' : 'text'

  switch (contentType) {
    case 'video': {
      return {
        type: contentType,
        videoHTML: video?.html as string,
        consentWall: consent.media.Wall,
        placeholder: image?.versions?.['large'].url ?? video?.thumbnail_url ?? '',
      }
    }

    case 'image': {
      return {
        type: contentType,
        src: image?.versions?.['large'].url ?? '',
      }
    }

    default:
      return undefined
  }
}

function getDisplayedDate(news: api.News, locale: api.Lang): string | undefined {
  if (news.status === api.NewsStatus.ONLINE) {
    return dateUtils.format(dateUtils.parseISO(news.date_publication ?? news.date_creation), 'PPP')
  }

  if (news.status === api.NewsStatus.WAITING) {
    return t('Draft (only visible by you)')
  }

  if (news.status === api.NewsStatus.SCHEDULED && news.date_schedule) {
    const yesterday = dateFns.sub(new Date(), { days: 1 })

    const dateSchedule = dateUtils.parseISO(news.date_schedule)

    const isDateScheduleAfterYesterday = dateFns.isAfter(dateSchedule, yesterday)

    const formattedScheduledDate = isDateScheduleAfterYesterday
      ? dateUtils.format(dateSchedule, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')
      : ` ${t('on')} ${dateUtils.format(dateSchedule, 'P')}`

    return t('Post scheduled for %(date)s at %(time)s', {
      date: formattedScheduledDate,
      time: dateUtils.format(dateSchedule, 'HH:mm'),
    })
  }
}

function getLowestPriceRewards(news: api.News, project: api.Project): number | null {
  const userHasReadAccess = Boolean(news.permissions.self?.includes('read'))

  return !userHasReadAccess
    ? news.audience_type === api.NewsAudienceType.PAYING
      ? project.lowest_contribution_amount
      : (news.rewards?.reduce((acc, reward) => {
          if (acc === -1) {
            return reward.price
          }
          if (reward.price < acc) {
            return reward.price
          }
          return acc
        }, -1) ?? null)
    : null
}
